import type { CurrencyCode } from '@klear/constants/currency'
import { KlearDate } from '@klear/date'
import { Money } from '@klear/money'
import times from 'lodash/times'

export type Granularity = 'month' | 'quarter'
export type Lookback = '6m' | '1y' | 'all'

export enum Series {
  billed = 'billed',
  collected = 'collected'
}

export interface Bucket {
  label: string
  billed: Money
  collected: Money
}

interface AggregateInput {
  purchaseOrders: { status: string; amount: Money; effectiveDate: KlearDate }[]
  invoices: { status: string; amount: Money; effectiveDate: KlearDate; endDate: KlearDate }[]
  selectedCurrency: CurrencyCode
  granularity: Granularity
  lookback: Lookback
}

const LOOKBACK_SPAN: Record<Exclude<Lookback, 'all'>, { amount: number; unit: 'month' | 'year' }> = {
  '6m': { amount: 6, unit: 'month' },
  '1y': { amount: 1, unit: 'year' }
}

function lookbackWindow(
  now: KlearDate,
  lookback: Lookback,
  purchaseOrders: AggregateInput['purchaseOrders'],
  invoices: AggregateInput['invoices']
): { start: KlearDate; end: KlearDate } {
  if (lookback !== 'all') {
    const { amount, unit } = LOOKBACK_SPAN[lookback]
    return { start: now.subtract(amount, unit), end: now.add(amount, unit) }
  }
  const [first, ...rest] = [
    ...purchaseOrders.map((po) => po.effectiveDate),
    ...invoices.flatMap((invoice) => [invoice.effectiveDate, invoice.endDate])
  ]
  if (!first) return { start: now, end: now }
  let start = first
  let end = first
  for (const date of rest) {
    if (date.isBefore(start)) start = date
    if (date.isAfter(end)) end = date
  }
  return { start, end }
}

function bucketKeyFor(date: KlearDate, granularity: Granularity): string {
  if (granularity === 'month') return date.startOf('month').toString('YYYY-MM')
  const start = date.startOf('quarter')
  return `${start.yearValue}-Q${start.quarterValue}`
}

export function bucketLabelFor(date: KlearDate, granularity: Granularity): string {
  if (granularity === 'month') return date.toString('MMM YY')
  const start = date.startOf('quarter')
  return `Q${start.quarterValue} ${start.toString('YY')}`
}

function bucketCountFor(start: KlearDate, end: KlearDate, granularity: Granularity): number {
  if (granularity === 'month') return end.startOf('month').diff(start.startOf('month'), 'month') + 1
  return end.startOf('quarter').diff(start.startOf('quarter'), 'month') / 3 + 1
}

function buildEmptyBuckets(
  start: KlearDate,
  count: number,
  granularity: Granularity,
  currency: CurrencyCode
): Map<string, Bucket> {
  const step = granularity === 'month' ? 1 : 3
  const anchor = granularity === 'quarter' ? start.startOf('quarter') : start.startOf('month')
  const buckets = new Map<string, Bucket>()
  times(count, (i) => {
    const date = anchor.add(i * step, 'month')
    buckets.set(bucketKeyFor(date, granularity), {
      label: bucketLabelFor(date, granularity),
      billed: Money.zero(currency),
      collected: Money.zero(currency)
    })
  })
  return buckets
}

export function aggregate(
  { purchaseOrders, invoices, selectedCurrency, granularity, lookback }: AggregateInput,
  now: KlearDate = KlearDate.today()
) {
  const { start, end } = lookbackWindow(now, lookback, purchaseOrders, invoices)
  const bucketCount = bucketCountFor(start, end, granularity)
  const buckets = buildEmptyBuckets(start, bucketCount, granularity, selectedCurrency)
  const inCurrency = (money: Money): boolean => money.currency === selectedCurrency
  const inWindow = (date: KlearDate): boolean => date.isSameOrAfter(start, 'day') && date.isSameOrBefore(end, 'day')

  const addTo = (field: Series, date: KlearDate, money: Money) => {
    if (money.isZero()) return
    const bucket = buckets.get(bucketKeyFor(date, granularity))
    if (bucket) bucket[field] = bucket[field].add(money)
  }

  let totalOrderAmount = Money.zero(selectedCurrency)
  let totalInvoiced = Money.zero(selectedCurrency)

  for (const po of purchaseOrders) {
    if (!inCurrency(po.amount)) continue
    if (inWindow(po.effectiveDate)) totalOrderAmount = totalOrderAmount.add(po.amount)
  }

  for (const invoice of invoices) {
    if (!inCurrency(invoice.amount)) continue
    if (invoice.effectiveDate.isAfter(now, 'day')) continue

    if (inWindow(invoice.effectiveDate)) totalInvoiced = totalInvoiced.add(invoice.amount)
    addTo(Series.billed, invoice.effectiveDate, invoice.amount)
    addTo(Series.collected, invoice.endDate, invoice.amount)
  }

  return { totalOrderAmount, totalInvoiced, buckets }
}
